import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, InputAdornment, TextField, Box, Stack, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useGetClientRowQuery } from '@/features/clients/clientsApi';
import { useGetInsightPoliciesInfiniteQuery } from '@/features/policies/policiesApi';
import { formatDate } from '@/utils/constants';
import { safelyParseDate } from '@/utils/date';

export const PolicySelect = ({ clientId, policy, setPolicy, showRenewalDate, disabled }) => {
	const [open, setOpen] = useState(false);

	const { data: clientRow } = useGetClientRowQuery(clientId, { skip: !clientId });
	const insightId = clientRow?.insightId;
	const skipQuery = !insightId || Object.keys(policy ?? {}).length > 0;

	const {
		data,
		fetchNextPage,
		hasNextPage,
		isLoading,
		isFetching,
		isFetchingNextPage,
		isUninitialized,
	} = useGetInsightPoliciesInfiniteQuery({ clientId: insightId }, { skip: skipQuery });

	useEffect(() => {
		if (
			skipQuery ||
			isUninitialized ||
			!hasNextPage ||
			isFetching ||
			!data?.pages?.length
		) {
			return;
		}

		fetchNextPage();
	}, [skipQuery, isUninitialized, data?.pages?.length, hasNextPage, isFetching, fetchNextPage]);

	const policyOptions = useMemo(
		() => data?.pages.flatMap((page) => page.Results ?? []) ?? [],
		[data],
	);

	const isLoadingPolicies = isLoading || isFetchingNextPage || (isFetching && hasNextPage);

	return (
		<Autocomplete
			id='policy-select'
			loading={isLoadingPolicies}
			loadingText='Loading policies...'
			options={policyOptions}
			getOptionLabel={(policy) =>
				`${policy.Description}, ${policy.TypeDescription}, ${
					policy.PolicyNumber
				} - ${formatDate(safelyParseDate(policy.ToDate ?? policy.PolicyToDate))}`
			}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			renderOption={(props, policy) => (
				<Box component='li' {...props}>
					<Stack width='100%'>
						<Stack direction='row' justifyContent={'space-between'} width='100%'>
							<Typography sx={{ fontWeight: 500 }}>{policy.Description}</Typography>
							<Typography variant='task_updated'>
								{policy.PolicyNumber}
							</Typography>
						</Stack>
						{/* {(policy?.policyTransactions ?? []).length > 0 && (
							<Typography sx={{ fontSize: '12px' }}>{`Invoice: ${policy?.policyTransactions?.[0]?.InvoiceNumber}`}</Typography>
						)} */}
						<Stack direction='row' justifyContent={'space-between'} width='100%'>
							<Typography>{`${policy.TypeDescription}, ${policy.ClassOfRisk}`}</Typography>
							{(policy.ToDate ?? policy.PolicyToDate) && (
								<Typography variant='task_updated'>
									{'Renewal date: '}
									{formatDate(
										safelyParseDate(policy.ToDate ?? policy.PolicyToDate),
									)}
								</Typography>
							)}
						</Stack>
					</Stack>
				</Box>
			)}
			value={Object.keys(policy ?? {}).length > 0 ? policy : null}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder='Search for policy'
					// sx={{ opacity: !policyData ? '0.5' : '1' }}
					InputProps={{
						...params.InputProps,
						startAdornment: (
							<InputAdornment position='start'>
								{isLoadingPolicies ? (
									<CircularProgress color='inherit' size={20} />
								) : null}
							</InputAdornment>
						),
					}}
				/>
			)}
			isOptionEqualToValue={(option, value) => option.Id === value.Id}
			onChange={(_, value) => {
				setPolicy(value);
			}}
			disabled={!clientId || disabled}
		/>
	);
};
